import React, { useEffect } from 'react';
import { PageHeader } from '../components/page/PageHeader';
import { FeatureRow } from '../components/page/FeatureRow';
import { CtaBand } from '../components/page/CtaBand';
import { PlayCircle } from 'lucide-react';
import { Counter } from '../components/ui/Counter';
import { Reveal } from '../components/ui/Reveal';
import { LayeredPartnerRosterMockup, LayeredAListWedgeMockup, LayeredAgencyOutreachMockup } from '../components/page/MarketingMockups';
import { HowMarketplaceWorks } from '../components/page/HowMarketplaceWorks';
import { Testimonials, BRAND_TESTIMONIALS } from '../components/Testimonials';
const DEMO_VIDEO_URL = 'https://www.youtube.com/watch?v=U2sV81k41u4&pp=0gcJCUwLAYcqIYzv';
const SEO = {
  title: 'Partner With Talent Agencies More Efficiently | Zano for Marketing Agencies',
  description: 'Search live talent agency rosters, discover represented creators, collaborate with talent agencies at scale, and source creators more efficiently across every client campaign.',
  keywords: 'marketing agency software, influencer sourcing, talent agency directory, live talent agency rosters, represented creators, influencer workflow, partner with talent agencies, creator sourcing',
  ogTitle: 'Keep your talent agency partners front and center',
  ogDescription: 'Collaborate with talent agencies from one live database of agency rosters.'
};
function updateMetaTag(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const existingTag = document.head.querySelector<HTMLMetaElement>(selector);
  const tag = existingTag ?? document.createElement('meta');
  const previousContent = existingTag?.getAttribute('content') ?? null;
  if (!existingTag) {
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
  return () => {
    if (!existingTag) {
      tag.remove();
      return;
    }
    if (previousContent === null) {
      existingTag.removeAttribute('content');
    } else {
      existingTag.setAttribute('content', previousContent);
    }
  };
}
export const ForMarketingAgencies = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;
    const restoreDescription = updateMetaTag('name', 'description', SEO.description);
    const restoreKeywords = updateMetaTag('name', 'keywords', SEO.keywords);
    const restoreOgTitle = updateMetaTag('property', 'og:title', SEO.ogTitle);
    const restoreOgDescription = updateMetaTag('property', 'og:description', SEO.ogDescription);
    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreKeywords();
      restoreOgTitle();
      restoreOgDescription();
    };
  }, []);
  return <>
      <PageHeader eyebrow="For Marketing Agencies" eyebrowClass="bg-purple-50 text-purple-700 border border-purple-200" title="Keep your talent agency partners front and center" subtitle="Zano makes it easy and quick to keep the talent agency rosters you already partner with in front of you for quick reference when building campaigns. Plus, always keep in touch with the newest talent agencies." primaryCta={{
      label: 'Start Free Trial',
      to: '/pricing'
    }} secondaryCta={{
      label: 'Watch demo video',
      href: DEMO_VIDEO_URL,
      newTab: true,
      icon: <PlayCircle size={18} className="mr-1.5" data-id="element-2340" data-mp="y0owylg2h1jo" />
    }} data-id="element-2339" data-mp="chxwu7apffim" />

      {/* Metrics strip */}
      <section className="border-y border-gray-100 bg-gray-50/60 py-10" data-id="element-2341" data-mp="12yqf945o4wu3">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-id="element-2342" data-mp="17qroeh7ji7fw">
          {[{
          v: <Counter to={400} suffix="+" data-id="element-2343" data-mp="1cpbaqiq1pl8s" />,
          l: 'Talent agencies'
        }, {
          v: <Counter to={6000} suffix="+" data-id="element-2344" data-mp="1gpmwxzy23tmd" />,
          l: 'Represented creators'
        }, {
          v: <Counter to={100} suffix="" data-id="element-2345" data-mp="18429x0m8j9kv" />,
          l: 'Avg submissions / 24h'
        }, {
          v: <Counter to={500} prefix="$" suffix="k+" data-id="element-2346" data-mp="14308cd4uilfq" />,
          l: 'GMV generated'
        }].map((s, i) => <Reveal key={i} delay={i * 0.08} data-id="element-2347" data-mp="1htdjspgy4i9t" data-mpi={i}>
              <p className="text-3xl font-bold text-zano-pink" data-id="element-2348" data-mp="1cv296citu3qj">{s.v}</p>
              <p className="text-sm text-gray-600 mt-1" data-id="element-2349" data-mp="1cwvhdw938w0n">{s.l}</p>
            </Reveal>)}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 divide-y divide-gray-100" data-id="element-2350" data-mp="l3w4dis5vhux">
        <FeatureRow eyebrow="Partner Management" title="Keep partner rosters accessible" body="Already work with top talent agencies? Keep track of their live rosters on Zano instead of asking for updated PDFs every month. Quickly reference them when building campaigns for clients so you can move fast." bullets={['Filter the database by specific talent agencies', 'Always see their most up-to-date roster', 'Move faster on quick-turnaround client campaigns']} visual={<LayeredPartnerRosterMockup data-id="element-2352" data-mp="3shqtlpc54gyc" />} data-id="element-2351" data-mp="z3dy8ckbdlb5" />
        <FeatureRow eyebrow="The Wedge" title="A-list access for your brand clients" body="Access to A-list talent gives your agency a powerful wedge for your brand clients. Suddenly, you know exactly how to get in touch with the highest level of creators and have direct contact with their talent managers." bullets={['Direct access to talent managers for top creators', 'Impress clients with premium talent options', 'No more guessing who represents who']} visual={<LayeredAListWedgeMockup data-id="element-2354" data-mp="674q5qzlan0o" />} flip data-id="element-2353" data-mp="3ego4f3pumqj7" />
        <FeatureRow eyebrow="Direct Outreach" title="Partner directly on deals" body="The talent agencies on Zano are here to get more brand deals for their talent, so they are happy to partner with you for your brand clients. Approach them directly by email or post a casting call to get custom submissions." bullets={['Direct email contact info for talent managers', 'Post casting calls to receive custom quotes', 'Build long-term relationships with top agencies']} visual={<LayeredAgencyOutreachMockup data-id="element-2356" data-mp="pmeesh1syp49" />} data-id="element-2355" data-mp="tya420lyz58c" />
      </div>

      <HowMarketplaceWorks audience="you" data-id="element-2357" data-mp="pnxua5fmww4z" />

      <Testimonials title="What marketing agencies are saying" items={BRAND_TESTIMONIALS} data-id="element-2358" data-mp="kvy1qy1w95if" />

      <CtaBand title="Build client campaigns faster" subtitle="Join the marketing agencies using Zano to keep their talent partners close." primaryCta={{
      label: 'Start Free Trial',
      to: '/pricing'
    }} secondaryCta={{
      label: 'Watch demo video',
      href: DEMO_VIDEO_URL,
      newTab: true,
      icon: <PlayCircle size={18} className="mr-1.5" data-id="element-2360" data-mp="2dk5psxel5jvv" />
    }} data-id="element-2359" data-mp="fw1xqyt75e2v" />
    </>;
};